import { requestTimelineEntryEdit } from "../utils/requestTimelineEntryEdit";
import CareRecordHistoryButton from '../../../features/sourceLifecycle/CareRecordHistoryButton';
import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Drawer,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import colors from '../../../assets/theme/colors';
import { LOG_TYPES, getLogTypeByCategory } from '../../../constants/logTypeRegistry';
import { CARE_PROFILE_ROUTE } from '../../../constants/profileRoutes';
import { auth, db } from '../../../services/firebase';
import TimelineTimeLabel from '../TimelineTimeLabel';
import TimelineEntryShell from '../TimelineEntryShell';
import DefaultTimelineBody from '../DefaultTimelineBody';
import useLinkedSourceAudio from '../hooks/useLinkedSourceAudio';
import MedicationTimingPatternCard from '../MedicationTimingPatternCard';
import { getBehaviorDetails } from '../entryTransformers';
import CaptureFirstContextDetails from '../CaptureFirstContextDetails';
import TimelineMultipleMomentsIndicator from '../TimelineMultipleMomentsIndicator';
import LogTimeField from '../../UI/LogTimeField';
import { getCaptureFirstTimelineContext } from '../utils/captureFirstTimelineContext';
import { buildMedicationReviewNavigationState } from '../../../utils/medicationReviewNavigation';
import { buildProviderReviewNavigationState } from '../../../utils/providerReviewNavigation';
import useTimelineMedicationReview from '../hooks/useTimelineMedicationReview';
import TimelineMedicationReviewActions from '../TimelineMedicationReviewActions';
import TimelineMedicationReviewSheet from '../TimelineMedicationReviewSheet';
import useMedicationAdministrationReview from '../hooks/useMedicationAdministrationReview';
import MedicationAdministrationReviewActions from '../MedicationAdministrationReviewActions';
import MedicationAdministrationReviewSheet from '../MedicationAdministrationReviewSheet';
import { getCaregiverDisplayName, getTimelineLoggedByLabel } from '../utils/timelineCaregiverLabels';
import {
  canManageCareRecordPrivacy,
  getToggledCareRecordVisibility,
  isCareRecordPrivate,
} from '../../../services/timeline/recordVisibility';

const TEXT_PRIMARY = '#1F2937';
const TEXT_SECONDARY = '#4B5563';
const TEXT_MUTED = '#9CA3AF';
const VERIFY_AMBER = colors.semantic.warning || '#F59E0B';

const REVIEW_CATEGORY_OPTIONS = [
  'medication',
  'food',
  'sleep',
  'behavior',
  'activity',
  'mood',
  'bathroom',
  'log',
].map((value) => {
  const type = getLogTypeByCategory(value);
  return {
    value,
    label: type.displayLabel || type.filterLabel || value,
  };
});

const normalizeText = (value) => String(value || '').trim();

const condenseRepeatedWords = (value = '') => {
  let text = normalizeText(value);
  if (!text) {
    return '';
  }

  let previous = '';
  while (text !== previous) {
    previous = text;
    text = text.replace(/\b([\w'’-]+)(?:\s+\1\b)+/gi, '$1');
  }

  return text.replace(/\s+/g, ' ').trim();
};

const getCaregiverInitials = (value = '') => {
  const normalizedValue = String(value || '').trim();
  const sourceValue = normalizedValue.toLowerCase() === 'you'
    ? (auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || auth.currentUser?.email || normalizedValue)
    : normalizedValue;

  const parts = String(sourceValue || '')
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0]
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`
    .toUpperCase()
    .slice(0, 2);
};

const coerceDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const dateValue = value.toDate();
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimeInputValue = (dateValue) => {
  const date = coerceDateValue(dateValue);
  if (!date) {
    return '';
  }

  return format(date, 'HH:mm');
};

const applyTimeToDate = (baseDate, timeValue) => {
  const sourceDate = coerceDateValue(baseDate) || new Date();
  const text = String(timeValue || '').trim();
  if (!text) {
    return sourceDate;
  }

  const match = text.match(/^(\d{1,2})(?::(\d{2}))?(?:\s*([ap]m))?$/i);
  if (!match) {
    return sourceDate;
  }

  let hours = Number(match[1]);
  const minutes = Number(match[2] || 0);
  const meridiem = String(match[3] || '').toLowerCase() || null;

  if (meridiem === 'pm' && hours < 12) {
    hours += 12;
  } else if (meridiem === 'am' && hours === 12) {
    hours = 0;
  }

  const nextDate = new Date(sourceDate);
  nextDate.setHours(hours, minutes, 0, 0);
  return nextDate;
};

const getReviewedByPayload = () => ({
  uid: auth.currentUser?.uid || null,
  name: auth.currentUser?.displayName || auth.currentUser?.email || null,
  email: auth.currentUser?.email || null,
});

const PRIVATE_VISIBILITY_HELPER_TEXT = 'Only Care Owners and Partners can see this.';

const TimelineEntryRow = ({
  entryId,
  entryData = null,
  entryConfig = {},
  isLast = false,
  showActions = false,
  onEditEntry = null,
  onEditOriginal = null,
  onDeleteEntry = null,
  actionMenuDisabled = false,
  isEditing = false,
  editValue = '',
  onEditValueChange = null,
  onSaveEdit = null,
  onCancelEdit = null,
  nested = false,
  layoutVariant = 'default',
  caregiverLabel = '',
  currentUserRole = null,
  showCaptureFirstOriginalNote = true,
  showCaptureFirstContextDetails = true,
  suppressPendingMedicationReviewAction = false,
  sourceDetailsNode = null,
  storyParentExpanded = false,
  onStoryParentToggle = null,
}) => {
  const {
    label = 'Log',
    time = null,
    subtype = '',
    primaryText = '',
    insight = '',
    triggerSummary = '',
    notesText = '',
    foodReviewFlag = false,
    foodReviewMessage = '',
    initials = '',
    accentColor = '#64748B',
    kind = 'generic',
    detailRows = [],
  } = entryConfig;
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname || ''}${location.search || ''}` || '/dashboard';

  const isMoodNoteStyle = kind === 'mood'
    && (
      entryConfig.label === 'Mood note'
      || entryConfig.categoryLabel === 'Mood note'
      || Boolean(entryConfig.source?.parsedObservation)
    );
  const isMedicationRow = kind === 'meds';
  const isMedicationChangeRow = kind === 'medication_change';
  const isMedicationProfileActivity = [
    entryData?.type,
    entryData?.timelineType,
  ].some((value) => value === 'medication_review_annotation')
    || ['medication', 'medication_change', 'meds'].includes(kind);
  const showEntryActions = showActions && (kind !== 'mood' || isMoodNoteStyle);
  const isStoryLayout = layoutVariant === 'story';
  const isFeedLayout = layoutVariant === 'feed';
  const sourceMeta = entryConfig.sourceMeta || null;
  const caregiverDisplayName = entryConfig.caregiverDisplayName || getCaregiverDisplayName(entryData) || '';
  const caregiverInitials = (initials || '') || getCaregiverInitials(caregiverDisplayName);
  const loggedByLabel = getTimelineLoggedByLabel({
    entry: entryData,
    displayName: caregiverDisplayName,
    initials: caregiverLabel || caregiverInitials,
  });
  const categoryChipLabel = entryConfig.categoryLabel || label || 'Log';
  const entrySourceCategory = String(entryConfig.source?.cat || kind || '').toLowerCase();
  const sourceType = String(
    entryConfig.sourceMeta?.type
    || entryConfig.source?.type
    || entryConfig.source?.sourceType
    || ''
  ).trim().toLowerCase();
  const isParsedNoteSource = Boolean(
    sourceType !== 'manual'
    && (
      entryConfig.parsedObservation
      || entryConfig.source?.parsedObservation
      || entryConfig.sourceText
      || entryConfig.originalText
    )
  );
  const [sourceDrawerMode, setSourceDrawerMode] = React.useState(null);
  const [reviewEditMode, setReviewEditMode] = React.useState(false);
  const [reviewDraft, setReviewDraft] = React.useState(null);
  const [reviewSaving, setReviewSaving] = React.useState(false);
  const [privacySaving, setPrivacySaving] = React.useState(false);
  const presentation = entryConfig.presentation || {};
  const sourcePresentation = presentation.sourcePresentation || null;
  const statusPresentation = presentation.status || {};
  const sourceIsEmail = Boolean(sourcePresentation?.isEmail && (sourceMeta?.senderName || sourceMeta?.body || sourceMeta?.subject));
  const sourceNeedsReview = Boolean(sourcePresentation?.needsReview || sourceMeta?.needsVerification);
  const sourceChip = React.useMemo(() => {
    if (sourceIsEmail) {
      const sourceLabel = sourcePresentation?.label || sourceMeta?.senderName || 'School';
      return {
        label: `📧 ${sourceLabel}`,
        icon: null,
        kind: 'email',
      };
    }

    if (sourcePresentation?.isParsed || isParsedNoteSource || sourceType === 'import') {
      return {
        label: 'Parsed',
        icon: DescriptionOutlinedIcon,
        kind: 'parsed',
      };
    }

    return null;
  }, [isParsedNoteSource, sourceIsEmail, sourceMeta?.senderName, sourcePresentation?.isParsed, sourcePresentation?.label, sourceType]);
  const sourceEntryDate = React.useMemo(
    () => coerceDateValue(entryConfig.timestampValue || entryConfig.sourceMeta?.receivedAt || null),
    [entryConfig.sourceMeta?.receivedAt, entryConfig.timestampValue]
  );
  const sourceDrawerOpen = Boolean(sourceDrawerMode);
  const pointerNavigationPath = String(entryConfig.navigationTarget || '').trim();
  const handleHeaderClick = pointerNavigationPath && kind !== 'story_moment_reference'
    ? () => navigate(pointerNavigationPath)
    : null;
  const careLogCategoryLabel = entryConfig.careLogMeta?.label || '';
  const reviewCategoryLabel = React.useMemo(() => {
    if (careLogCategoryLabel) {
      return careLogCategoryLabel;
    }

    const matched = REVIEW_CATEGORY_OPTIONS.find((option) => option.value === entrySourceCategory);
    if (matched) {
      return matched.label;
    }

    if (kind === 'meds') return LOG_TYPES.medication.displayLabel;
    if (kind === 'medication_change') return LOG_TYPES.medication_change.displayLabel;
    if (kind === 'sleep') return LOG_TYPES.sleep.displayLabel;
    if (kind === 'food') return LOG_TYPES.food.displayLabel;
    if (kind === 'behavior') return LOG_TYPES.behavior.displayLabel;
    if (kind === 'activity') return LOG_TYPES.activity.displayLabel;
    if (kind === 'mood') return LOG_TYPES.mood.displayLabel;
    if (kind === 'toilet') return LOG_TYPES.bathroom.displayLabel;
    return categoryChipLabel || 'Log';
  }, [careLogCategoryLabel, categoryChipLabel, entrySourceCategory, kind]);
  const renderSourcePill = () => {
    if (!sourceChip) {
      return null;
    }

    return (
      <Chip
        onClick={(event) => {
          if (sourceChip.kind === 'email') {
            event.stopPropagation();
            openSourceDrawer('source');
          }
        }}
        label={(
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            {sourceChip.icon ? <sourceChip.icon sx={{ fontSize: 13, color: 'inherit', flexShrink: 0 }} /> : null}
            <span>{sourceChip.label}</span>
          </Stack>
        )}
        size="small"
        variant="outlined"
        sx={{
          height: 22,
          px: 0.35,
          fontSize: '0.64rem',
          fontWeight: 700,
          letterSpacing: '0.01em',
          color: colors.semantic.ink2,
          bgcolor: sourceChip.kind === 'manual'
            ? alpha(colors.semantic.sageMist, 0.40)
            : sourceChip.kind === 'parsed'
              ? alpha(colors.semantic.sageMist, 0.34)
              : alpha(colors.semantic.context.bg, 0.42),
          borderColor: alpha(colors.semantic.context.ink, 0.10),
          maxWidth: 150,
          '& .MuiChip-label': {
            px: 0.45,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    );
  };

  const renderVerifyChip = () => {
    if (!sourceNeedsReview) {
      return null;
    }

    return (
      <Chip
        onClick={(event) => {
          event.stopPropagation();
          openSourceDrawer('review');
        }}
        label="Verify"
        size="small"
        variant="outlined"
        sx={{
          height: 22,
          px: 0.35,
          fontSize: '0.64rem',
          fontWeight: 800,
          letterSpacing: '0.02em',
          color: VERIFY_AMBER,
          bgcolor: alpha(VERIFY_AMBER, 0.10),
          borderColor: alpha(VERIFY_AMBER, 0.22),
          maxWidth: 120,
          '& .MuiChip-label': {
            px: 0.45,
          },
        }}
      />
    );
  };

  const formatSourceReceivedTime = React.useMemo(() => {
    if (!sourceMeta?.receivedAt) {
      return '';
    }

    const receivedDate = sourceMeta.receivedAt instanceof Date
      ? sourceMeta.receivedAt
      : new Date(sourceMeta.receivedAt);

    if (Number.isNaN(receivedDate.getTime())) {
      return '';
    }

    return format(receivedDate, 'MMM d, yyyy · h:mm a');
  }, [sourceMeta?.receivedAt]);

  const handleEditClick = React.useCallback(() => requestTimelineEntryEdit({
    entry: entryData, entryConfig, entryId, onEditEntry, onEditOriginal,
  }), [entryConfig, entryData, entryId, onEditEntry, onEditOriginal]);

  const handleDeleteClick = React.useCallback(() => {
    onDeleteEntry?.();
  }, [onDeleteEntry]);

  const entryCollection = entryData?.collection || entryConfig.source?.collection || 'dailyLogs';
  const canTogglePrivacy = entryCollection === 'dailyLogs' && canManageCareRecordPrivacy(currentUserRole);
  const isPrivate = isCareRecordPrivate(entryData);
  const handleTogglePrivacy = React.useCallback(async () => {
    if (!entryId || !canTogglePrivacy || privacySaving) {
      return;
    }

    setPrivacySaving(true);

    try {
      const entryRef = doc(db, entryCollection, entryId);
      await updateDoc(entryRef, {
        visibility: getToggledCareRecordVisibility(entryData),
        updatedAt: serverTimestamp(),
        privacyUpdatedAt: serverTimestamp(),
        privacyUpdatedBy: auth.currentUser?.uid || null,
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: {
            childId: entryData?.childId || entryConfig.source?.childId || null,
            entryId,
          },
        }));
      }
    } catch (privacyError) {
      console.error('Error updating timeline entry privacy:', privacyError);
    } finally {
      setPrivacySaving(false);
    }
  }, [canTogglePrivacy, entryCollection, entryConfig.source?.childId, entryData, entryId, privacySaving]);

  const buildReviewDraft = React.useCallback(() => {
    const initialDate = sourceEntryDate || new Date();
    const draftCategoryValue = (() => {
      const sourceCategory = entrySourceCategory;
      if (REVIEW_CATEGORY_OPTIONS.some((option) => option.value === sourceCategory)) {
        return sourceCategory;
      }

      if (kind === 'meds') return 'medication';
      if (kind === 'medication_change') return 'medication_change';
      if (kind === 'sleep') return 'sleep';
      if (kind === 'food') return 'food';
      if (kind === 'behavior') return 'behavior';
      if (kind === 'activity') return 'activity';
      if (kind === 'mood') return 'mood';
      if (kind === 'toilet') return 'bathroom';
      return 'log';
    })();

    return {
      title: normalizeText(
        entryConfig.label
        || entryConfig.primaryText
        || entryConfig.triggerSummary
        || entryConfig.notesText
        || entryConfig.subtype
        || reviewCategoryLabel
      ) || reviewCategoryLabel,
      category: draftCategoryValue,
      note: normalizeText(
        entryConfig.notesText
        || entryConfig.primaryText
        || entryConfig.insight
        || entryConfig.triggerSummary
        || entryConfig.label
        || ''
      ),
      time: formatTimeInputValue(initialDate),
    };
  }, [entryConfig.insight, entryConfig.label, entryConfig.notesText, entryConfig.primaryText, entryConfig.subtype, entryConfig.triggerSummary, entrySourceCategory, kind, reviewCategoryLabel, sourceEntryDate]);

  const openSourceDrawer = React.useCallback((mode = 'source') => {
    if (mode === 'review') {
      setReviewDraft(buildReviewDraft());
    } else {
      setReviewDraft(null);
    }

    setReviewEditMode(false);
    setSourceDrawerMode(mode);
  }, [buildReviewDraft]);

  const closeSourceDrawer = React.useCallback(() => {
    setSourceDrawerMode(null);
    setReviewEditMode(false);
    setReviewDraft(null);
    setReviewSaving(false);
  }, []);

  const handleApproveReview = React.useCallback(async (nextDraft = null) => {
    if (!entryId) {
      return;
    }

    const draft = nextDraft || reviewDraft || buildReviewDraft();
    const baseDate = sourceEntryDate || new Date();
    const nextDate = applyTimeToDate(baseDate, draft.time || formatTimeInputValue(baseDate));
    const reviewer = getReviewedByPayload();

    setReviewSaving(true);

    try {
      const entryRef = doc(db, 'dailyLogs', entryId);
      const updatePayload = {
        title: normalizeText(draft.title) || normalizeText(draft.note) || reviewCategoryLabel,
        titlePrefix: normalizeText(draft.title) || normalizeText(draft.note) || reviewCategoryLabel,
        text: normalizeText(draft.note) || normalizeText(draft.title) || '',
        content: normalizeText(draft.note) || normalizeText(draft.title) || '',
        note: normalizeText(draft.note) || normalizeText(draft.title) || '',
        category: draft.category || entrySourceCategory || entryConfig.kind || entryConfig.category || 'log',
        updatedAt: new Date(),
        source: {
          ...(entryConfig.source || {}),
          verified: true,
          reviewedAt: serverTimestamp(),
          reviewedBy: reviewer,
        },
      };

      if (nextDate) {
        updatePayload.timestamp = nextDate;
        updatePayload.entryDate = nextDate.toDateString();
        updatePayload.entryDayKey = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
      }

      if (draft.category && draft.category !== 'behavior') {
        updatePayload.type = draft.category === 'log' ? 'dailyLog' : draft.category;
        updatePayload.timelineType = draft.category === 'log' ? 'dailyLog' : draft.category;
        updatePayload.entryType = 'dailyLog';
      }

      if (entryConfig.kind === 'behavior' || draft.category === 'behavior') {
        updatePayload.incidentStyle = true;
        updatePayload.incidentCategoryId = 'behavior';
        updatePayload.incidentCategoryLabel = 'Behavior';
        updatePayload.category = 'behavior';
        updatePayload.type = 'behavior';
        updatePayload.timelineType = 'incident';
        updatePayload.entryType = 'incident';
        updatePayload.notes = normalizeText(draft.note) || updatePayload.notes || '';
        updatePayload.incidentData = {
          ...(entryConfig.incidentData || {}),
          description: updatePayload.title,
          notes: normalizeText(draft.note) || updatePayload.notes || '',
        };
      }

      await updateDoc(entryRef, updatePayload);
      closeSourceDrawer();
    } catch (saveError) {
      console.error('Error reviewing timeline source entry:', saveError);
    } finally {
      setReviewSaving(false);
    }
  }, [buildReviewDraft, closeSourceDrawer, entryConfig.category, entryConfig.incidentData, entryConfig.kind, entryConfig.source, entryId, entrySourceCategory, reviewCategoryLabel, reviewDraft, sourceEntryDate]);

  const handleDeleteReviewEntry = React.useCallback(() => {
    closeSourceDrawer();
    handleDeleteClick();
  }, [closeSourceDrawer, handleDeleteClick]);

  const updateReviewDraftField = React.useCallback((field, value) => {
    setReviewDraft((prev) => ({
      ...(prev || buildReviewDraft()),
      [field]: value,
    }));
  }, [buildReviewDraft]);

  const behaviorDetails = kind === 'behavior'
    ? getBehaviorDetails(entryData || {}, entryData?.contextSnapshot || entryData?.incidentData?.contextSnapshot || {})
    : null;
  const behaviorDetailRows = behaviorDetails?.rows || [];
  const behaviorContextFlags = behaviorDetails?.contextFlags || [];
  const mediaUrls = Array.isArray(entryConfig.mediaUrls) ? entryConfig.mediaUrls : [];
  const storedAudioAttachments = Array.isArray(entryConfig.audioAttachments) ? entryConfig.audioAttachments : [];
  const linkedSourceNoteId = String(
    entryData?.sourceNoteId || entryData?.metadata?.sourceNoteId || entryData?.source?.sourceNoteId || ''
  ).trim();
  const hasVoiceTranscript = /\bVoice transcript:\s*/i.test(String(
    entryData?.rawSourceText || entryData?.rawText || entryData?.text || entryData?.description || ''
  ));
  const linkedSourceAudio = useLinkedSourceAudio({
    sourceNoteId: linkedSourceNoteId,
    enabled: hasVoiceTranscript && storedAudioAttachments.length === 0,
  });
  const audioAttachments = storedAudioAttachments.length ? storedAudioAttachments : linkedSourceAudio;
  const captureFirstContext = React.useMemo(
    () => getCaptureFirstTimelineContext(entryData || {}, entryConfig || {}),
    [entryConfig, entryData]
  );
  const hasCaptureFirstContext = Boolean(captureFirstContext?.hasVisibleContent);
  const resolvedTitle = React.useMemo(() => normalizeText(
    entryConfig.displayTitle
    || entryConfig.title
    || entryConfig.titlePrefix
    || label
    || (
      kind === 'sleep'
        ? LOG_TYPES.sleep.displayLabel
        : kind === 'behavior'
          ? LOG_TYPES.behavior.displayLabel
          : kind === 'food'
            ? LOG_TYPES.food.displayLabel
            : kind === 'mood'
              ? LOG_TYPES.mood.displayLabel
              : kind === 'activity'
                ? LOG_TYPES.activity.displayLabel
                : kind === 'meds'
                  ? LOG_TYPES.medication.displayLabel
                  : kind === 'medication_change'
                    ? LOG_TYPES.medication.displayLabel
                    : kind === 'toilet'
                      ? LOG_TYPES.bathroom.displayLabel
                      : 'Log'
    )
  ) || 'Log', [entryConfig.displayTitle, entryConfig.title, entryConfig.titlePrefix, kind, label]);
  const titleAccessoryNode = presentation.hasMultipleMoments ? (
    <TimelineMultipleMomentsIndicator
      momentCount={presentation.momentCount}
      compact={isFeedLayout || !isStoryLayout}
    />
  ) : null;
  const doctorVisitProviderSnapshot = entryData?.providerSnapshot || entryConfig.providerSnapshot || null;
  const doctorVisitNeedsProviderReview = kind === 'doctor_visit'
    && !entryData?.providerId;
  const timelineMedicationReview = useTimelineMedicationReview({
    entryId,
    entryData,
    entryConfig,
    captureFirstContext,
    currentPath,
  });
  const hasPendingMedicationReview = timelineMedicationReview.isPending;
  const medicationAdministrationReview = useMedicationAdministrationReview({
    entryId,
    entryData,
  });
  const hasPendingMedicationAdministrationReview = medicationAdministrationReview.isPending;
  const hasMedicationAdministrationReview = Boolean(medicationAdministrationReview.status);
  const hasPendingMedicationDetailReview = hasPendingMedicationReview
    && !hasMedicationAdministrationReview;
  const medicationNeedsReview = hasPendingMedicationDetailReview
    || kind === 'medication'
    || kind === 'medication_change'
    || kind === 'meds';
  const handleOpenMedicationProfile = React.useCallback(() => {
    const childId = entryData?.childId || entryConfig.source?.childId || '';
    navigate(CARE_PROFILE_ROUTE, {
      state: {
        childId,
        openChildMedicationManager: {
          childId,
          medicationId: medicationAdministrationReview.confirmedAdministrations?.[0]?.medicationId
            || medicationAdministrationReview.review?.medicationId
            || entryData?.medicationId
            || null,
        },
      },
    });
  }, [entryConfig.source?.childId, entryData?.childId, entryData?.medicationId, medicationAdministrationReview.confirmedAdministrations, medicationAdministrationReview.review?.medicationId, navigate]);
  const handleReviewMedication = React.useCallback(() => {
    if (hasPendingMedicationAdministrationReview || medicationAdministrationReview.status === 'confirmed') {
      medicationAdministrationReview.openReview();
      return;
    }
    if (hasPendingMedicationDetailReview) {
      timelineMedicationReview.open();
      return;
    }
    if (!medicationNeedsReview) {
      return;
    }

    navigate(CARE_PROFILE_ROUTE, {
      state: {
        ...buildMedicationReviewNavigationState({
          childId: entryData?.childId || entryConfig.source?.childId || '',
          returnTo: currentPath,
          originEntryId: entryId || entryData?.id || entryData?.sourceNoteId || '',
          originDate: entryData?.timestamp || entryData?.visitDateTime || entryData?.date || entryData?.createdAt || entryData?.selectedDate || null,
          originSourceId: entryData?.sourceNoteId || entryData?.sourceId || entryData?.id || entryId || '',
          sourceText: captureFirstContext?.originalNote || entryData?.body || entryData?.note || entryData?.text || '',
        }),
        openChildMedicationManager: {
          childId: entryData?.childId || entryConfig.source?.childId || null,
        },
      },
    });
  }, [captureFirstContext?.originalNote, currentPath, entryConfig.source?.childId, entryData, entryId, hasPendingMedicationAdministrationReview, hasPendingMedicationDetailReview, medicationAdministrationReview, medicationNeedsReview, navigate, timelineMedicationReview]);
  const handleReviewProvider = React.useCallback(() => {
    if (!doctorVisitNeedsProviderReview) {
      return;
    }

    navigate('/providers', {
      state: buildProviderReviewNavigationState({
        childId: entryData?.childId || '',
        returnTo: currentPath,
        originEntryId: entryId || entryData?.doctorVisitId || entryData?.id || '',
        originDate: entryData?.visitDateTime || entryData?.visitAt || entryData?.visitDate || entryData?.timestamp || null,
        originSourceId: entryData?.doctorVisitId || entryData?.id || entryData?.providerSnapshot?.providerId || '',
        visitId: entryData?.doctorVisitId || entryData?.id || '',
        providerName: entryData?.providerName || entryData?.doctorName || doctorVisitProviderSnapshot?.doctorName || '',
        providerSpecialty: entryData?.providerSpecialty || entryData?.specialty || doctorVisitProviderSnapshot?.specialty || '',
        organizationName: doctorVisitProviderSnapshot?.organizationName || '',
        doctorVisit: entryData || null,
      }),
    });
  }, [currentPath, doctorVisitNeedsProviderReview, doctorVisitProviderSnapshot, entryData, entryId, navigate]);
  const supportingMetaNode = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.45}
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        flexWrap: 'wrap',
        overflow: 'hidden',
      }}
    >
      {(time || (onEditEntry && !actionMenuDisabled)) ? (
        <TimelineTimeLabel
          time={time}
          onEdit={onEditEntry && !actionMenuDisabled ? handleEditClick : undefined}
          sx={{
            fontSize: isStoryLayout ? { xs: '0.66rem', sm: '0.72rem' } : '0.72rem',
            fontWeight: 400,
            color: alpha(TEXT_SECONDARY, isStoryLayout ? 0.66 : 0.8),
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        />
      ) : null}

      {time ? <Typography component="span" sx={{ fontSize: '0.66rem', color: alpha(TEXT_SECONDARY, 0.36), lineHeight: 1, mx: 0.02, flexShrink: 0 }}>·</Typography> : null}
      {loggedByLabel ? (
        <Typography
          component="span"
          sx={{
            fontSize: isStoryLayout ? { xs: '0.66rem', sm: '0.72rem' } : '0.7rem',
            fontWeight: 400,
            color: alpha(TEXT_SECONDARY, isStoryLayout ? 0.66 : 0.8),
            lineHeight: 1.2,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {`${entryConfig.attributionVerb || 'Logged by'} ${loggedByLabel}`}
        </Typography>
      ) : null}

      {(statusPresentation.isEdited || statusPresentation.isPrivate || isPrivate) ? (
        <>
          {time ? <Typography component="span" sx={{ fontSize: '0.66rem', color: alpha(TEXT_SECONDARY, 0.36), lineHeight: 1, mx: 0.02, flexShrink: 0 }}>·</Typography> : null}
          <Typography
            component="span"
            sx={{
              fontSize: isStoryLayout ? { xs: '0.66rem', sm: '0.72rem' } : '0.7rem',
              fontWeight: 600,
              color: alpha(TEXT_SECONDARY, 0.72),
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {[statusPresentation.isEdited ? 'Edited' : null, (statusPresentation.isPrivate || isPrivate) ? 'Private' : null].filter(Boolean).join(' · ')}
          </Typography>
        </>
      ) : null}

      {sourceChip ? (
        <>
          {(time || statusPresentation.isEdited || statusPresentation.isPrivate) ? <Typography component="span" sx={{ fontSize: '0.66rem', color: alpha(TEXT_SECONDARY, 0.36), lineHeight: 1, mx: 0.02, flexShrink: 0 }}>·</Typography> : null}
          {renderSourcePill()}
        </>
      ) : null}

      {sourceNeedsReview ? (
        <>
          {(time || statusPresentation.isEdited || statusPresentation.isPrivate || sourceChip) ? <Typography component="span" sx={{ fontSize: '0.66rem', color: alpha(TEXT_SECONDARY, 0.36), lineHeight: 1, mx: 0.02, flexShrink: 0 }}>·</Typography> : null}
          {renderVerifyChip()}
        </>
      ) : null}
    </Stack>
  );
  const resolvedSourceDetailsNode = typeof sourceDetailsNode === 'function'
    ? sourceDetailsNode({ onReviewMedication: handleReviewMedication })
    : sourceDetailsNode;
  const narrativeBodyText = normalizeText(
    kind === 'meds' && entryConfig.attributionVerb === 'Recorded by'
      ? entryConfig.bodyText
      : kind === 'story_moment_reference'
      ? (
        entryData?.body
        || entryData?.notes
        || entryData?.note
        || entryData?.description
        || entryData?.summary
        || entryData?.content
        || entryData?.text
        || entryConfig.sourceMeta?.body
        || sourceMeta?.body
        || entryConfig.bodyText
        || ''
      )
      : (
        entryData?.body
        || entryData?.notes
        || entryData?.note
        || entryData?.description
        || entryData?.summary
        || entryData?.content
        || entryData?.text
        || entryConfig.sourceMeta?.body
        || sourceMeta?.body
        || entryConfig.bodyText
        || entryConfig.notesText
        || entryConfig.insight
        || entryConfig.triggerSummary
        || ''
      )
  );
  const summaryText = React.useMemo(() => {
    if (kind === 'behavior') {
      return normalizeText(primaryText || triggerSummary || '');
    }

    return normalizeText(narrativeBodyText || primaryText || (kind === 'generic' ? notesText : '') || triggerSummary || '');
  }, [kind, narrativeBodyText, notesText, primaryText, triggerSummary]);
  const behaviorWhatHappenedText = React.useMemo(() => normalizeText(
    condenseRepeatedWords(
      entryData?.body
      || entryData?.incidentData?.description
      || entryData?.description
      || entryData?.title
      || entryData?.text
      || summaryText
      || ''
    )
  ), [entryData?.description, entryData?.incidentData?.description, entryData?.text, entryData?.title, summaryText]);
  const behaviorBodySummaryText = behaviorWhatHappenedText || summaryText;
  const narrativeDisplayText = normalizeText(
    narrativeBodyText
    || entryConfig.bodyText
    || entryConfig.body
    || entryConfig.sourceMeta?.body
    || entryConfig.notesText
    || entryConfig.primaryText
    || entryConfig.insight
    || entryConfig.triggerSummary
    || ''
  );
  const behaviorRowsToRender = React.useMemo(() => {
    if (kind !== 'behavior') {
      return behaviorDetailRows;
    }

    const normalizedSummary = normalizeText(behaviorBodySummaryText || summaryText);
    const summaryCompacted = normalizedSummary.toLowerCase().replace(/\s+/g, ' ');
    const isDuplicateBehaviorValue = (value) => {
      const normalizedValue = normalizeText(value);
      if (!normalizedValue) {
        return true;
      }

      const lowered = normalizedValue.toLowerCase().replace(/\s+/g, ' ');
      if (!summaryCompacted) {
        return false;
      }

      return lowered === summaryCompacted
        || lowered.includes(summaryCompacted)
        || summaryCompacted.includes(lowered);
    };

    return behaviorDetailRows.filter((detail) => !isDuplicateBehaviorValue(detail?.value));
    }, [behaviorBodySummaryText, behaviorDetailRows, kind, summaryText]);
  const foodRowsToRender = React.useMemo(() => {
    if (kind !== 'food') {
      return detailRows;
    }

    if (!foodReviewFlag) {
      return detailRows;
    }

    return (Array.isArray(detailRows) ? detailRows : []).map((row) => {
      const rowLabel = normalizeText(row?.label).toLowerCase();
      if (rowLabel === 'reaction') {
        return {
          ...row,
          valueColor: '#B42318',
        };
      }

      return row;
    });
  }, [detailRows, foodReviewFlag, kind]);
  const hasDenseBodyContent = Boolean(
    behaviorBodySummaryText
    || narrativeBodyText
    || summaryText
    || insight
    || notesText
    || (kind === 'food' ? foodRowsToRender?.length : detailRows?.length)
    || (behaviorRowsToRender && behaviorRowsToRender.length)
    || hasCaptureFirstContext
  );
  const compactCard = isFeedLayout
    || ((kind !== 'mood' || isMoodNoteStyle) && !hasDenseBodyContent);
  const medicationTimingPatterns = React.useMemo(() => (
    Array.isArray(entryData?.medicationTimingPatterns)
      ? entryData.medicationTimingPatterns
      : Array.isArray(entryConfig.medicationTimingPatterns)
        ? entryConfig.medicationTimingPatterns
        : []
  ), [entryConfig.medicationTimingPatterns, entryData?.medicationTimingPatterns]);
  const medicationTimingPatternContext = React.useMemo(() => {
    const firstPattern = medicationTimingPatterns[0] || null;
    const medicationChange = firstPattern?.medicationChange || {};

    return {
      medicationName: normalizeText(entryData?.medicationName || entryData?.medicationDetails?.medicationName || firstPattern?.medicationName || ''),
      medicationChangeType: normalizeText(entryData?.medicationChangeType || medicationChange.medicationChangeType || ''),
      effectiveAt: normalizeText(entryData?.effectiveFrom || entryData?.actualDateTime || medicationChange.effectiveAt || ''),
    };
  }, [entryData?.actualDateTime, entryData?.effectiveFrom, entryData?.medicationChangeType, entryData?.medicationDetails?.medicationName, entryData?.medicationName, medicationTimingPatterns]);
  const hasVisibleBodyContent = Boolean(
    behaviorBodySummaryText
    || narrativeBodyText
    || (kind !== 'behavior' && summaryText)
    || (kind !== 'sleep' && insight)
    || (kind === 'generic' && notesText)
    || (kind === 'behavior' && behaviorRowsToRender?.length)
    || (kind === 'sleep' && detailRows?.length)
    || (kind !== 'behavior' && kind !== 'sleep' && detailRows?.length)
    || hasCaptureFirstContext
    || (kind === 'medication_change' && medicationTimingPatterns.length)
  );
  const primaryNarrativeBodyText = normalizeText(
    kind === 'story_moment_reference'
      ? (
        narrativeBodyText
        || entryConfig.bodyText
        || entryConfig.sourceMeta?.body
        || entryData?.body
        || entryData?.content
        || entryData?.notes
        || entryData?.note
        || entryData?.description
        || entryData?.summary
        || entryData?.text
        || ''
      )
      : (
        narrativeBodyText
        || entryConfig.bodyText
        || entryConfig.sourceMeta?.body
        || entryData?.body
        || entryConfig.notesText
        || entryData?.content
        || entryData?.notes
        || entryData?.note
        || entryData?.description
        || entryData?.summary
        || entryData?.text
        || ''
      )
  );
  const structuredBodyHasContent = Boolean(
    kind === 'story_moment_reference'
      ? (narrativeBodyText || entryConfig.triggerSummary || notesText)
      : (
        behaviorBodySummaryText
        || (kind !== 'behavior' && summaryText)
        || (kind !== 'sleep' && insight)
        || (kind === 'generic' && notesText)
        || (kind === 'behavior' && behaviorRowsToRender?.length)
        || (kind === 'sleep' && detailRows?.length)
        || (kind !== 'behavior' && kind !== 'sleep' && detailRows?.length)
        || (kind === 'food' && foodReviewFlag)
        || hasCaptureFirstContext
      )
  );
  const bodyNode = isEditing ? (
    <TextField
      fullWidth
      multiline
      minRows={2}
      value={editValue}
      onChange={(event) => onEditValueChange?.(event.target.value)}
      placeholder="Edit note..."
      size="small"
      sx={{
        mt: 0.1,
        '& .MuiInputBase-root': {
          bgcolor: '#FFFFFF',
          borderRadius: 1.5,
        },
      }}
    />
  ) : (
    <Box
      sx={{
        minWidth: 0,
        pt: isStoryLayout ? { xs: 1.2, sm: 1.3 } : { xs: 1.15, sm: 1.25 },
      }}
    >
      {primaryNarrativeBodyText ? (
        <Typography
          sx={{
            fontSize: isStoryLayout ? { xs: '0.94rem', sm: '0.98rem' } : { xs: '0.84rem', sm: '0.88rem' },
            lineHeight: isStoryLayout ? 1.6 : 1.5,
            color: isStoryLayout ? alpha(TEXT_PRIMARY, 0.95) : TEXT_PRIMARY,
            fontWeight: 400,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {primaryNarrativeBodyText}
        </Typography>
      ) : null}

      {kind === 'story_moment_reference' && onStoryParentToggle ? (
        <Button
          variant="text"
          onClick={onStoryParentToggle}
          aria-expanded={storyParentExpanded}
          sx={{
            mt: 0.55,
            p: 0,
            minWidth: 0,
            justifyContent: 'flex-start',
            color: colors.brand.deep,
            fontSize: '0.76rem',
            fontWeight: 750,
            textTransform: 'none',
          }}
        >
          {`Part of: ${normalizeText(entryData?.storyParentLabel || 'Story')}`}
        </Button>
      ) : null}

      {structuredBodyHasContent ? (
        <Box sx={{ mt: primaryNarrativeBodyText ? 0.65 : 0 }}>
          {(() => {
            const normalizedPrimaryBody = normalizeText(primaryNarrativeBodyText).toLowerCase();
            const structuredSummaryText = normalizeText(
              kind === 'story_moment_reference'
                ? ''
                : kind === 'behavior'
                  ? behaviorBodySummaryText
                  : (
                    narrativeDisplayText
                    || normalizeText(primaryText || (kind === 'generic' ? notesText : ''))
                  )
            );
            const normalizedStructuredSummary = structuredSummaryText.toLowerCase();
            const summaryShouldRender = Boolean(
              structuredSummaryText
              && normalizedStructuredSummary
              && (
                !normalizedPrimaryBody
                || (
                  normalizedStructuredSummary !== normalizedPrimaryBody
                  && !normalizedStructuredSummary.includes(normalizedPrimaryBody)
                  && !normalizedPrimaryBody.includes(normalizedStructuredSummary)
                )
              )
            );
            const structuredNotesText = normalizeText(
              kind === 'generic'
                ? normalizeText(entryData?.body || entryConfig.bodyText || entryConfig.sourceMeta?.body || notesText)
                : normalizeText(
                    entryConfig.notesText
                    || entryConfig.triggerSummary
                    || ''
                  )
            );
            const normalizedStructuredNotes = structuredNotesText.toLowerCase();
            const notesShouldRender = Boolean(
              structuredNotesText
              && (
                !normalizedPrimaryBody
                || (
                  normalizedStructuredNotes !== normalizedPrimaryBody
                  && !normalizedStructuredNotes.includes(normalizedPrimaryBody)
                  && !normalizedPrimaryBody.includes(normalizedStructuredNotes)
                )
              )
            );

            return (
          <DefaultTimelineBody
            summaryText={summaryShouldRender ? structuredSummaryText : ''}
            insight={kind === 'behavior' ? '' : insight}
            notesText={notesShouldRender ? structuredNotesText : ''}
            detailRows={kind === 'behavior' ? behaviorRowsToRender : (kind === 'food' ? foodRowsToRender : detailRows)}
            mediaUrls={mediaUrls}
            audioAttachments={audioAttachments}
            nested={nested}
            isMedicationRow={isMedicationRow}
            isMedicationChangeRow={isMedicationChangeRow}
            compact={compactCard}
            story={isStoryLayout}
            summaryLines={kind === 'behavior' ? 0 : 2}
            notesAsMetadata={kind === 'story_moment_reference'}
            reviewBanner={kind === 'food' && foodReviewFlag ? {
              label: 'Allergy concern flagged',
              message: foodReviewMessage || 'This log will be marked for caregiver review',
            } : null}
          />
            );
          })()}
        </Box>
      ) : null}

      {kind === 'medication_change' && medicationTimingPatterns.length ? (
        <MedicationTimingPatternCard
          medicationName={medicationTimingPatternContext.medicationName}
          medicationChangeType={medicationTimingPatternContext.medicationChangeType}
          effectiveAt={medicationTimingPatternContext.effectiveAt}
          patterns={medicationTimingPatterns}
        />
      ) : null}

      {!suppressPendingMedicationReviewAction
        && !hasMedicationAdministrationReview
        && ['pending', 'resolved', 'dismissed'].includes(timelineMedicationReview.status) ? (
        <TimelineMedicationReviewActions
          status={timelineMedicationReview.status}
          opening={timelineMedicationReview.opening}
          onReview={handleReviewMedication}
          onDismiss={timelineMedicationReview.dismiss}
        />
      ) : null}

      {['pending', 'confirmed', 'dismissed'].includes(medicationAdministrationReview.status)
        && !(suppressPendingMedicationReviewAction && medicationAdministrationReview.status === 'pending')
        && !(hasCaptureFirstContext && showCaptureFirstContextDetails) ? (
        <MedicationAdministrationReviewActions controller={medicationAdministrationReview} onOpenMedicationProfile={handleOpenMedicationProfile} />
      ) : null}

      {hasCaptureFirstContext && showCaptureFirstContextDetails ? (
        <CaptureFirstContextDetails
          entryData={entryData || {}}
          entryConfig={entryConfig || {}}
          context={captureFirstContext}
          story={isStoryLayout}
          showOriginalNote={showCaptureFirstOriginalNote}
          parentCategoryKey={kind}
          onAddDetails={onEditEntry ? () => onEditEntry(entryData) : null}
          onReviewMedication={(hasPendingMedicationAdministrationReview || hasPendingMedicationDetailReview)
            ? handleReviewMedication
            : null}
          onReviewProvider={doctorVisitNeedsProviderReview ? handleReviewProvider : null}
          medicationReviewNode={null}
        />
      ) : null}
      {hasCaptureFirstContext && showCaptureFirstContextDetails
        && medicationAdministrationReview.status === 'confirmed' ? (
        <MedicationAdministrationReviewActions controller={medicationAdministrationReview} onOpenMedicationProfile={handleOpenMedicationProfile} />
      ) : null}
      <CareRecordHistoryButton entry={{ ...entryData, id: entryId, collection: entryCollection }} />
      {resolvedSourceDetailsNode}
    </Box>
  );

  return (
    <>
      <TimelineEntryShell
        id={entryId ? `timeline-entry-${entryId}` : undefined}
        dataEntryId={entryId || undefined}
        ariaLabel={`${resolvedTitle}${time ? ` at ${time}` : ''}`}
        typeKey={
          kind === 'meds' || kind === 'medication_change'
            ? 'medication'
            : kind === 'toilet'
              ? 'bathroom'
              : kind === 'log'
                ? 'log'
                : kind
        }
        presentation={presentation}
        title={resolvedTitle}
        metaNode={supportingMetaNode}
        titleAccessoryNode={titleAccessoryNode}
        time={null}
        actor={null}
        headerExtras={null}
        tags={[]}
        menuActions={showEntryActions ? [
          ...(canTogglePrivacy ? [{
            label: isPrivate ? 'Private' : 'Mark private',
            description: PRIVATE_VISIBILITY_HELPER_TEXT,
            checked: isPrivate,
            onClick: handleTogglePrivacy,
            disabled: privacySaving,
          }] : []),
          ...(doctorVisitNeedsProviderReview ? [{
            label: 'Review provider',
            onClick: handleReviewProvider,
          }] : []),
          isMedicationProfileActivity
            ? {
                label: 'Medication profile',
                description: 'View or edit the medication list.',
                icon: <EditIcon />,
                onClick: handleOpenMedicationProfile,
              }
            : {
                label: 'Edit',
                icon: <EditIcon />,
                onClick: handleEditClick,
                disabled: !onEditEntry,
              },
          {
            label: 'Delete',
            icon: <DeleteIcon />,
            onClick: handleDeleteClick,
            disabled: !onDeleteEntry,
            danger: true,
          },
        ] : []}
        showActions={showEntryActions}
        actionMenuDisabled={actionMenuDisabled || (!onEditEntry && !onDeleteEntry && !canTogglePrivacy)}
        accentColor={accentColor}
        variant={isStoryLayout ? 'story' : (isFeedLayout ? 'feed' : 'default')}
        nested={nested}
        compact={compactCard}
        contentSpacing={isStoryLayout ? 0.72 : 0.14}
        presentation={presentation}
        titleAccessoryNode={titleAccessoryNode}
        metaNode={supportingMetaNode}
        bodyNode={bodyNode}
        onHeaderClick={handleHeaderClick}
      >
      </TimelineEntryShell>

      <TimelineMedicationReviewSheet
        review={timelineMedicationReview}
        childName={entryData?.childName || ''}
      />
      <MedicationAdministrationReviewSheet
        controller={medicationAdministrationReview}
        childName={entryData?.childName || ''}
      />

      {isEditing ? (
        <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0, alignItems: 'flex-start', mt: 0.1, pl: 0.5 }}>
          <IconButton
            size="small"
            onClick={onSaveEdit}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 0.75,
              bgcolor: alpha('#059669', 0.1),
              color: '#059669',
              border: '1px solid',
              borderColor: alpha('#059669', 0.22),
            }}
            aria-label="Save edit"
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onCancelEdit}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 0.75,
              bgcolor: '#FFFFFF',
              color: TEXT_MUTED,
              border: '1px solid',
              borderColor: 'divider',
            }}
            aria-label="Cancel edit"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ) : null}

      <Drawer
        anchor="bottom"
        open={sourceDrawerOpen}
        onClose={closeSourceDrawer}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            px: 2,
            pt: 1.5,
            pb: 2.5,
            maxHeight: '78vh',
          },
        }}
      >
        {sourceDrawerMode === 'review' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: colors.semantic.ink }}>
                  Verify extracted entry
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink2, mt: 0.25 }}>
                  Quick check only for email entries that need a second look.
                </Typography>
              </Box>
              <IconButton
                onClick={closeSourceDrawer}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: colors.semantic.sageMist,
                  color: colors.semantic.ink2,
                  flexShrink: 0,
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.1,
                bgcolor: '#FAFCFB',
                border: `1px solid ${colors.semantic.line}`,
                borderRadius: 3,
                p: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Title
                </Typography>
                {reviewEditMode ? (
                  <TextField
                    value={reviewDraft?.title || ''}
                    onChange={(event) => updateReviewDraftField('title', event.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: '#FFFFFF',
                        borderRadius: 1.5,
                        fontWeight: 700,
                      },
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: colors.semantic.ink }}>
                    {reviewDraft?.title || entryConfig.label || 'Message from school'}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                    Category
                  </Typography>
                  {reviewEditMode ? (
                    <TextField
                      select
                      value={reviewDraft?.category || 'log'}
                      onChange={(event) => updateReviewDraftField('category', event.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: '#FFFFFF',
                          borderRadius: 1.5,
                        },
                      }}
                    >
                      {REVIEW_CATEGORY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 650, color: colors.semantic.ink }}>
                      {reviewCategoryLabel}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                    Time
                  </Typography>
                  {reviewEditMode ? (
                    <LogTimeField
                      value={reviewDraft?.time || ''}
                      onChange={(event) => updateReviewDraftField('time', event.target.value)}
                      showLabel={false}
                    />
                  ) : (
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 650, color: colors.semantic.ink }}>
                      {entryConfig.time || (sourceEntryDate ? format(sourceEntryDate, 'h:mm a') : 'Unknown')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Note
                </Typography>
                {reviewEditMode ? (
                  <TextField
                    value={reviewDraft?.note || ''}
                    onChange={(event) => updateReviewDraftField('note', event.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Add a short note..."
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: '#FFFFFF',
                        borderRadius: 1.5,
                      },
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.9rem',
                      lineHeight: 1.55,
                      color: colors.semantic.ink,
                    }}
                  >
                    {reviewDraft?.note || entryConfig.primaryText || entryConfig.notesText || 'No note provided.'}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Source
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', color: colors.semantic.ink }}>
                  {sourceMeta?.senderName || 'School'}
                </Typography>
                {sourceMeta?.senderEmail ? (
                  <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink2 }}>
                    {sourceMeta.senderEmail}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink2 }}>
                  {sourceMeta?.subject || 'Message'}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink2 }}>
                  {formatSourceReceivedTime || 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Original message
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${colors.semantic.line}`,
                    borderRadius: 2,
                    p: 1.25,
                    maxHeight: '24vh',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.9rem',
                      lineHeight: 1.55,
                      color: colors.semantic.ink,
                    }}
                  >
                    {sourceMeta?.body || 'No message body available.'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} sx={{ pt: 0.25 }}>
              {reviewEditMode ? (
                <>
                  <Button
                    onClick={() => {
                      setReviewEditMode(false);
                      setReviewDraft(buildReviewDraft());
                    }}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      height: 44,
                      borderRadius: 999,
                      borderColor: colors.semantic.line,
                      color: colors.semantic.ink2,
                      fontWeight: 650,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleApproveReview(reviewDraft)}
                    variant="contained"
                    disabled={reviewSaving}
                    sx={{
                      flex: 2,
                      height: 44,
                      borderRadius: 999,
                      bgcolor: colors.semantic.sageDeep,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      boxShadow: `0 2px 6px ${alpha(colors.semantic.sageDeep, 0.25)}`,
                      '&:hover': {
                        bgcolor: colors.semantic.sageDeep,
                      },
                    }}
                  >
                    Save changes
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleDeleteReviewEntry}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      height: 44,
                      borderRadius: 999,
                      borderColor: colors.semantic.line,
                      color: colors.semantic.ink2,
                      fontWeight: 650,
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    onClick={() => setReviewEditMode(true)}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      height: 44,
                      borderRadius: 999,
                      borderColor: colors.semantic.line,
                      color: colors.semantic.ink,
                      fontWeight: 650,
                    }}
                  >
                    Edit fields
                  </Button>
                  <Button
                    onClick={() => handleApproveReview(reviewDraft)}
                    variant="contained"
                    disabled={reviewSaving}
                    sx={{
                      flex: 2,
                      height: 44,
                      borderRadius: 999,
                      bgcolor: colors.semantic.sageDeep,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      boxShadow: `0 2px 6px ${alpha(colors.semantic.sageDeep, 0.25)}`,
                      '&:hover': {
                        bgcolor: colors.semantic.sageDeep,
                      },
                    }}
                  >
                    Approve as-is
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: colors.semantic.ink }}>
                Original message
              </Typography>
              <IconButton
                onClick={closeSourceDrawer}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: colors.semantic.sageMist,
                  color: colors.semantic.ink2,
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: '#FAFCFB',
                border: `1px solid ${colors.semantic.line}`,
                borderRadius: 3,
                p: 1.5,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  From
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: colors.semantic.ink }}>
                  {sourceMeta?.senderName || 'School'}
                </Typography>
                {sourceMeta?.senderEmail ? (
                  <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink2 }}>
                    {sourceMeta.senderEmail}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Subject
                </Typography>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 650, color: colors.semantic.ink }}>
                  {sourceMeta?.subject || 'Message'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Received
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', color: colors.semantic.ink2 }}>
                  {formatSourceReceivedTime || 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.semantic.ink2 }}>
                  Original body
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${colors.semantic.line}`,
                    borderRadius: 2,
                    p: 1.25,
                    maxHeight: '34vh',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.9rem',
                      lineHeight: 1.55,
                      color: colors.semantic.ink,
                    }}
                  >
                    {sourceMeta?.body || 'No message body available.'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Drawer>
    </>
  );
};

export default React.memo(TimelineEntryRow);
